import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Col from "react-bootstrap/esm/Col";
import Form from "react-bootstrap/esm/Form";
import Row from "react-bootstrap/esm/Row";
import Spinner from "react-bootstrap/esm/Spinner";
import ChoirSong from "../../DTOs/ChoirSong";
import { loadPerformancesInRange } from "../../services/choirSongService";
import { monthsBefore, performancesCsv, repertoireStats } from "../../utils/repertoire";

interface RepertoireStatsProps {
    gemeindeId: string;
    songs: ChoirSong[];
    today: string;
    formatDate: (date: string) => string;
    onError: (message: string) => void;
}

const LIST_LENGTH = 8;

/** Most sung, long unsung, and the CSV export of a period. */
export default function RepertoireStats({ gemeindeId, songs, today, formatDate, onError }: RepertoireStatsProps) {
    const { t } = useTranslation();
    const stats = useMemo(() => repertoireStats(songs, today, LIST_LENGTH), [songs, today]);
    const [from, setFrom] = useState(monthsBefore(today, 12));
    const [to, setTo] = useState(today);
    const [exporting, setExporting] = useState(false);
    const [exportInfo, setExportInfo] = useState("");

    const handleExport = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!from || !to || from > to) return;
        setExporting(true);
        setExportInfo("");
        try {
            const names = new Map(songs.map((song) => [song.id, song.name.toString()]));
            const performances = await loadPerformancesInRange(gemeindeId, from, to);
            const csv = performancesCsv(
                performances.map((p) => ({
                    date: p.date,
                    song: names.get(p.songId) ?? p.songId,
                    occasion: p.occasion,
                    source: p.source.startsWith("service:") ? String(t("repertoire.sourceService")) : p.source,
                })),
                [String(t("repertoire.csvDate")), String(t("repertoire.csvSong")), String(t("repertoire.csvOccasion")), String(t("repertoire.csvSource"))]
            );
            const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = `chorlieder_${from}_${to}.csv`;
            link.click();
            URL.revokeObjectURL(url);
            setExportInfo(String(t("repertoire.exported", { count: performances.length })));
        } catch (err: any) {
            onError(err?.message || t("errors.anErrorOccurred"));
        } finally {
            setExporting(false);
        }
    };

    return (
        <Card className="mb-4 shadow-sm border-0">
            <Card.Body>
                <Row className="g-4">
                    <Col md={4}>
                        <h6>{t("repertoire.mostSung")}</h6>
                        {stats.mostSung.length === 0 ? (
                            <p className="text-muted small mb-0">{t("repertoire.noData")}</p>
                        ) : (
                            <ol className="small ps-3 mb-0">
                                {stats.mostSung.map(({ song, count }) => (
                                    <li key={song.id}>{song.name.toString()} <span className="text-muted">· {count}×</span></li>
                                ))}
                            </ol>
                        )}
                    </Col>
                    <Col md={4}>
                        <h6>{t("repertoire.longUnsungTitle", { count: stats.longUnsung.length })}</h6>
                        {stats.longUnsung.length === 0 ? (
                            <p className="text-muted small mb-0">{t("repertoire.noData")}</p>
                        ) : (
                            <ul className="small ps-3 mb-0">
                                {stats.longUnsung.slice(0, LIST_LENGTH).map(({ song, lastSung }) => (
                                    <li key={song.id}>
                                        {song.name.toString()}{" "}
                                        <span className="text-muted">· {lastSung ? formatDate(lastSung) : t("repertoire.never")}</span>
                                    </li>
                                ))}
                                {stats.longUnsung.length > LIST_LENGTH && (
                                    <li className="text-muted list-unstyled">{t("repertoire.more", { count: stats.longUnsung.length - LIST_LENGTH })}</li>
                                )}
                            </ul>
                        )}
                    </Col>
                    <Col md={4}>
                        <h6>{t("repertoire.exportTitle")}</h6>
                        <Form onSubmit={handleExport}>
                            <Row className="g-2 mb-2">
                                <Col xs={6}>
                                    <Form.Label className="small text-muted mb-1" htmlFor="repertoireExport.from">{t("repertoire.from")}</Form.Label>
                                    <Form.Control id="repertoireExport.from" type="date" size="sm" required max="9999-12-31" value={from} onChange={(e) => setFrom(e.target.value)} />
                                </Col>
                                <Col xs={6}>
                                    <Form.Label className="small text-muted mb-1" htmlFor="repertoireExport.to">{t("repertoire.to")}</Form.Label>
                                    <Form.Control id="repertoireExport.to" type="date" size="sm" required max="9999-12-31" min={from} value={to} onChange={(e) => setTo(e.target.value)} />
                                </Col>
                            </Row>
                            <Button type="submit" size="sm" variant="outline-primary" disabled={exporting}>
                                {exporting && <Spinner animation="border" size="sm" className="me-2" />}
                                {t("repertoire.export")}
                            </Button>
                            {exportInfo && <div className="small text-muted mt-2">{exportInfo}</div>}
                        </Form>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}
